import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { FunnelLayout } from '../../components/FunnelLayout';
import { SoWhatCallout } from '../../components/funnel/SoWhatCallout';
import { FunnelImage } from '../../components/funnel/FunnelImage';
import { useForm } from '../../context/FormContext';
import { useAppConfig } from '../../context/AppConfigContext';
import { getBreedInsight, getBreedWeakPoints } from '../../utils/breedMeta';
import { normalizeBreedKey, stripAccents } from '../../utils/normalize';
import { DOG_BREEDS } from '../../utils/breeds';
import { AgeCategory } from '../../types';
import type { BreedEntry } from '../../types/config';

interface CachorroAgeCaps {
  small: number;
  medium: number;
  large: number;
  giant: number;
}

const DEFAULT_CACHORRO_CAPS: CachorroAgeCaps = {
  small: 12,
  medium: 15,
  large: 18,
  giant: 24,
};

const MESTIZO_KEY = 'mestizo_cruce';
const MESTIZO_FALLBACK_DISPLAY = 'Mestizo / Cruce';
const SEARCH_RESULT_LIMIT = 8;

function cachorroMaxMonthsForBreed(
  entry: BreedEntry | undefined,
  caps: CachorroAgeCaps = DEFAULT_CACHORRO_CAPS,
): number {
  if (!entry) return caps.giant;
  const min = typeof entry.weight_min_kg === 'number' ? entry.weight_min_kg : null;
  const max = typeof entry.weight_max_kg === 'number' ? entry.weight_max_kg : null;
  const avg = min != null && max != null ? (min + max) / 2 : (max ?? min ?? 15);
  if (avg < 10) return caps.small;
  if (avg < 25) return caps.medium;
  if (avg < 40) return caps.large;
  return caps.giant;
}

interface IndexedBreed {
  key: string;
  entry: Partial<BreedEntry> & { display_name: string };
  normalizedName: string;
  firstLetter: string;
  source: 'remote' | 'fallback';
}

type Phase = 'picking' | 'confirmed';

const P3Raza: React.FC = () => {
  const { formData, updateFormData, goToNextStep, goToPreviousStep } = useForm();
  const { config, t } = useAppConfig();
  const db = config.breed_database;

  const fallbackBreeds = useMemo<IndexedBreed[]>(() => {
    return DOG_BREEDS.filter((displayName) => displayName !== 'No lo sé').map((displayName) => {
      const key = displayName === MESTIZO_FALLBACK_DISPLAY ? MESTIZO_KEY : normalizeBreedKey(displayName);
      const firstChar = stripAccents(displayName)[0]?.toUpperCase() ?? '';
      return {
        key,
        entry: {
          display_name: displayName,
          supplement_recommendations: [],
        },
        normalizedName: stripAccents(displayName.toLowerCase()),
        firstLetter: firstChar >= 'A' && firstChar <= 'Z' ? firstChar : '',
        source: 'fallback' as const,
      };
    });
  }, []);

  const allBreeds = useMemo<IndexedBreed[]>(() => {
    if (!db?.breeds || Object.keys(db.breeds).length === 0) return fallbackBreeds;
    const out: IndexedBreed[] = [];
    for (const [key, entry] of Object.entries(db.breeds)) {
      const displayName = typeof entry?.display_name === 'string' ? entry.display_name : '';
      const normalized = stripAccents(displayName.toLowerCase());
      const firstChar = stripAccents(displayName)[0]?.toUpperCase() ?? '';
      out.push({
        key,
        entry,
        normalizedName: normalized,
        firstLetter: firstChar >= 'A' && firstChar <= 'Z' ? firstChar : '',
        source: 'remote',
      });
    }
    return out;
  }, [db, fallbackBreeds]);

  const hasBreedDatabase = allBreeds.length > 0;
  const usingFallbackBreedDatabase = allBreeds.length > 0 && allBreeds.every((breed) => breed.source === 'fallback');

  const initialKey = formData.raza || null;
  const initialEntryKnown = initialKey
    ? Boolean(db?.breeds?.[initialKey] || allBreeds.some((breed) => breed.key === initialKey))
    : false;
  const initialUsesMestizoFallback = formData.breed_code === MESTIZO_KEY;
  const initialPhase: Phase =
    initialKey && (initialEntryKnown || initialKey === MESTIZO_KEY || initialUsesMestizoFallback)
      ? 'confirmed'
      : 'picking';

  const [query, setQuery] = useState('');
  const [isUnknownBreed, setIsUnknownBreed] = useState(false);
  const [selectedKey, setSelectedKey] = useState<string | null>(initialPhase === 'confirmed' ? initialKey : null);
  const [phase, setPhase] = useState<Phase>(initialPhase);
  const inputRef = useRef<HTMLInputElement>(null);
  const [attempted, setAttempted] = useState(false);

  const nombre = formData.nombre || 'tu perro';

  const searchResults = useMemo<IndexedBreed[]>(() => {
    const q = stripAccents(query.trim().toLowerCase());
    if (!q) return [];
    const out: IndexedBreed[] = [];
    for (const breed of allBreeds) {
      if (breed.normalizedName.includes(q)) {
        out.push(breed);
        if (out.length >= SEARCH_RESULT_LIMIT) break;
      }
    }
    return out;
  }, [allBreeds, query]);

  const handleSelectBreed = useCallback(
    (key: string) => {
      setSelectedKey(key);
      updateFormData({ raza: key, breed_code: key });
      setPhase('confirmed');
      setQuery('');
    },
    [updateFormData],
  );

  const handleSelectManualBreed = useCallback(() => {
    const manualBreedName = query.trim().replace(/\s+/g, ' ');
    if (!manualBreedName) return;
    setSelectedKey(manualBreedName);
    updateFormData({ raza: manualBreedName, breed_code: MESTIZO_KEY });
    setPhase('confirmed');
    setQuery('');
  }, [query, updateFormData]);

  const handleChangeBreed = useCallback(() => {
    setSelectedKey(null);
    setPhase('picking');
    setQuery('');
    setIsUnknownBreed(false);
  }, []);

  const handleQueryChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    setQuery(next);
  }, []);

  useEffect(() => {
    if (phase === 'picking') inputRef.current?.focus();
  }, [phase]);

  const canContinue = phase === 'confirmed' && selectedKey !== null;

  const handleContinue = useCallback(() => {
    if (canContinue) goToNextStep();
  }, [canContinue, goToNextStep]);

  const selectedFallbackEntry = selectedKey ? allBreeds.find((breed) => breed.key === selectedKey)?.entry : undefined;
  const selectedEntry: BreedEntry | undefined = selectedKey ? db?.breeds?.[selectedKey] : undefined;
  const breedInsight = selectedKey ? getBreedInsight(selectedKey, db) : null;
  const breedWeakPoints = selectedKey ? getBreedWeakPoints(selectedKey, db) : [];
  const breedDisplayName =
    selectedEntry?.display_name ??
    selectedFallbackEntry?.display_name ??
    (selectedKey === MESTIZO_KEY ? MESTIZO_FALLBACK_DISPLAY : (selectedKey ?? ''));
  const photoSrc = selectedKey ? `/images/breeds/${selectedKey}.png` : '';

  const ageTotalMonths = (formData.ageYears ?? 0) * 12 + (formData.ageMonths ?? 0);
  const cachorroCap = cachorroMaxMonthsForBreed(
    selectedEntry,
    config.nutrition_rules?.cachorro_age_caps_months || DEFAULT_CACHORRO_CAPS,
  );
  const showCachorroAdultMismatch =
    phase === 'confirmed' && formData.ageCategory === AgeCategory.CACHORRO && ageTotalMonths > cachorroCap;

  const handleSwitchToAdulto = useCallback(() => {
    updateFormData({ ageCategory: AgeCategory.ADULTO });
  }, [updateFormData]);

  const showSearch = phase === 'picking';
  const showBrowse = showSearch && query.trim() === '';

  const sidebarContent = (() => {
    if (phase !== 'confirmed') return null;
    if (isUnknownBreed) {
      return (
        <SoWhatCallout>
          {t(
            'p3.unknown_breed_message',
            '¡No te preocupes! Calcularemos el menú de {nombre} en base a su peso y lo que nos cuentes de él.',
            { nombre },
          )}
        </SoWhatCallout>
      );
    }
    if (selectedKey === MESTIZO_KEY) return null;
    if (breedWeakPoints.length > 0 || breedInsight) {
      return (
        <div className="space-y-2">
          {breedWeakPoints.length > 0 && (
            <>
              <p className="font-fredoka text-sm font-semibold text-black/70">
                {t('p3.weak_points_title', 'Puntos a vigilar en {raza}', { raza: breedDisplayName })}
              </p>
              <SoWhatCallout bullets={breedWeakPoints} />
            </>
          )}
          {breedWeakPoints.length === 0 && breedInsight && <SoWhatCallout>{breedInsight}</SoWhatCallout>}
        </div>
      );
    }
    return null;
  })();

  const missingReason = !canContinue ? t('p3.missing_breed', 'Selecciona la raza de {nombre}', { nombre }) : undefined;

  return (
    <FunnelLayout
      step={3}
      useTwoPhase
      onContinue={handleContinue}
      continueDisabled={!canContinue}
      continueDisabledReason={missingReason}
      onContinueDisabledAttempt={() => setAttempted(true)}
      onBack={goToPreviousStep}
      sidebar={sidebarContent}
    >
      <div className="flex flex-col gap-4">
        <h1 className="font-fredoka text-xl sm:text-2xl font-semibold text-center text-black">
          {t('p3.title', '¿Cuál es la raza de {nombre}?', { nombre })}
        </h1>

        {showSearch && !hasBreedDatabase && (
          <div className="bg-amber-50 border border-amber-300 rounded-[14px] p-3">
            <p className="font-poppins text-sm text-amber-800 text-center">
              {t(
                'p3.breed_db_unavailable',
                'No hemos podido cargar la lista de razas. Puedes continuar como Mestizo / Cruce y completar tu plan igual.',
              )}
            </p>
          </div>
        )}

        {showSearch && usingFallbackBreedDatabase && (
          <p className="font-poppins text-xs text-center text-black/45 -mt-2">
            {t(
              'p3.breed_db_fallback',
              'Si la lista completa tarda en cargar, puedes buscar igualmente entre las razas más comunes.',
            )}
          </p>
        )}

        {showSearch && (
          <div className="relative">
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={handleQueryChange}
              aria-label={t('p3.search_placeholder', 'Busca la raza de {nombre}...', { nombre })}
              placeholder={t('p3.search_placeholder', 'Busca la raza de {nombre}...', { nombre })}
              className={`w-full px-4 py-3 text-base font-poppins border-2 rounded-[14px] focus:outline-none focus:ring-2 transition-all ${
                attempted && !canContinue
                  ? 'border-red-500 focus:ring-red-500/30'
                  : 'border-[#008BBC] focus:ring-[#008BBC]/20'
              }`}
              autoComplete="off"
            />

            {query.trim().length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border-2 border-[#008BBC] rounded-[14px] overflow-hidden shadow-lg">
                {searchResults.length > 0 ? (
                  searchResults.map((breed) => (
                    <button
                      key={breed.key}
                      type="button"
                      onClick={() => handleSelectBreed(breed.key)}
                      className="w-full px-3 py-2 text-left font-poppins text-sm hover:bg-[#90D1E8]/20 transition-all text-black flex items-center gap-3"
                    >
                      <div className="w-9 h-9 rounded-[8px] overflow-hidden bg-[#FAF7F1] flex-shrink-0 relative">
                        <FunnelImage
                          src={`/images/breeds/${breed.key}.png`}
                          alt=""
                          className="absolute inset-0 w-full h-full object-contain p-0.5"
                          fallback={
                            <span
                              className="absolute inset-0 flex items-center justify-center text-lg"
                              aria-hidden="true"
                            >
                              🐾
                            </span>
                          }
                        />
                      </div>
                      <span>{breed.entry.display_name}</span>
                    </button>
                  ))
                ) : (
                  <div className="p-3 flex flex-col gap-2">
                    <p className="text-center font-poppins text-sm text-black/50">
                      {hasBreedDatabase
                        ? t('p3.no_results', 'No encontramos esa raza — prueba con otra búsqueda')
                        : t(
                            'p3.manual_breed_unavailable',
                            'No podemos buscar en la lista ahora mismo, pero puedes escribir la raza.',
                          )}
                    </p>
                    <button
                      type="button"
                      onClick={handleSelectManualBreed}
                      className="w-full px-3 py-2 rounded-[10px] bg-[#008BBC] text-white font-fredoka text-sm font-medium hover:bg-[#007aa8] transition-colors"
                    >
                      {t('p3.use_manual_breed', 'Usar "{raza}"', { raza: query.trim().replace(/\s+/g, ' ') })}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {showBrowse && (
          <div className="flex flex-col gap-3">
            {/* Mestizo y "No lo sé" resuelven al mismo key (mestizo_cruce) pero
                aparecen como dos chips distintos para que el usuario que no
                sabe la raza no se sienta forzado a "marcar" su perro como cruce. */}
            <div className="flex flex-wrap justify-center gap-2">
              <button
                type="button"
                onClick={() => {
                  handleSelectBreed(MESTIZO_KEY);
                  setIsUnknownBreed(false);
                }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-black/15 bg-white hover:bg-[#FAF7F1] transition-all"
              >
                <span aria-hidden="true">🐾</span>
                <span className="font-poppins text-sm text-black/70">{t('p3.mestizo_chip', 'Mestizo / Cruce')}</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  handleSelectBreed(MESTIZO_KEY);
                  setIsUnknownBreed(true);
                }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-black/15 bg-white hover:bg-[#FAF7F1] transition-all"
              >
                <span aria-hidden="true">❓</span>
                <span className="font-poppins text-sm text-black/70">{t('p3.unknown_chip', 'No lo sé')}</span>
              </button>
            </div>
          </div>
        )}

        {showCachorroAdultMismatch && (
          <div className="bg-amber-50 border border-amber-300 rounded-[14px] p-3 flex flex-col gap-2">
            <p className="font-poppins text-sm text-amber-800">
              {t(
                'p3.cachorro_adulto_mismatch',
                'Con {years}a {months}m, un {raza} ya suele ser adulto. ¿Lo cambiamos?',
                {
                  years: formData.ageYears ?? 0,
                  months: formData.ageMonths ?? 0,
                  raza: breedDisplayName,
                },
              )}
            </p>
            <div className="flex gap-2 justify-center flex-wrap">
              <button
                type="button"
                onClick={handleSwitchToAdulto}
                className="px-3 py-1.5 rounded-[10px] bg-[#008BBC] text-white font-fredoka text-xs font-medium hover:bg-[#007aa8] transition-colors"
              >
                {t('p3.switch_to_adulto', 'Sí, cambiar a adulto')}
              </button>
              <button
                type="button"
                onClick={goToPreviousStep}
                className="px-3 py-1.5 rounded-[10px] bg-white text-[#008BBC] border border-[#008BBC]/30 font-fredoka text-xs font-medium hover:bg-[#008BBC]/5 transition-colors"
              >
                {t('p3.back_to_age', 'Volver al paso de edad')}
              </button>
            </div>
          </div>
        )}

        {phase === 'confirmed' && selectedKey && (
          <div className="flex flex-col items-center gap-3 bg-[#FAF7F1] rounded-[20px] p-4">
            <div className="w-28 h-28 rounded-[16px] overflow-hidden bg-[#E9E3D7] relative">
              <FunnelImage
                src={photoSrc}
                alt={breedDisplayName}
                className="absolute inset-0 w-full h-full object-contain"
                fallback={
                  <span
                    className="absolute inset-0 flex items-center justify-center text-5xl"
                    role="img"
                    aria-label={breedDisplayName}
                  >
                    🐶
                  </span>
                }
              />
            </div>

            <p className="font-fredoka text-lg font-semibold text-black">{breedDisplayName}</p>

            {isUnknownBreed && (
              <div className="lg:hidden w-full">
                <SoWhatCallout>
                  {t(
                    'p3.unknown_breed_message',
                    '¡No te preocupes! Calcularemos el menú de {nombre} en base a su peso y lo que nos cuentes de él.',
                    { nombre },
                  )}
                </SoWhatCallout>
              </div>
            )}
            {!isUnknownBreed && selectedKey !== MESTIZO_KEY && (breedWeakPoints.length > 0 || breedInsight) && (
              <div className="lg:hidden w-full space-y-2">
                {breedWeakPoints.length > 0 && (
                  <>
                    <p className="font-fredoka text-sm font-semibold text-black/70">
                      {t('p3.weak_points_title', 'Puntos a vigilar en {raza}', { raza: breedDisplayName })}
                    </p>
                    <SoWhatCallout bullets={breedWeakPoints} />
                  </>
                )}
                {breedWeakPoints.length === 0 && breedInsight && <SoWhatCallout>{breedInsight}</SoWhatCallout>}
              </div>
            )}

            <button
              type="button"
              onClick={handleChangeBreed}
              className="font-fredoka text-sm text-[#008BBC] underline hover:text-[#007aa8] transition-colors mt-1"
            >
              {t('p3.change_breed', 'Cambiar raza')}
            </button>
          </div>
        )}
      </div>
    </FunnelLayout>
  );
};

export default P3Raza;
